import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import { charactersList } from '@/mocks/characters';
import { guidesList } from '@/mocks/guides';
import { eventsList } from '@/mocks/events';

interface Suggestion {
  label: string;
  to: string;
  type: string;
}

function buildIndex(): Suggestion[] {
  const items: Suggestion[] = [];
  newsList.forEach((item) => items.push({ label: item.title, to: `/news/${item.id}`, type: '资讯' }));
  eventsList.forEach((item) => items.push({ label: item.title, to: `/events/${item.id}`, type: '活动' }));
  charactersList.forEach((item) => items.push({ label: `${item.name} · 角色攻略`, to: `/characters/${item.id}`, type: '角色' }));
  guidesList.forEach((item) => items.push({ label: item.title, to: `/guides/${item.id}`, type: '攻略' }));
  return items;
}

interface SearchBarProps {
  defaultValue?: string;
  variant?: 'nav' | 'hero';
  autoFocus?: boolean;
}

export default function SearchBar({ defaultValue = '', variant = 'nav', autoFocus = false }: SearchBarProps) {
  const [value, setValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);
  const index = useMemo(buildIndex, []);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const suggestions = useMemo(() => {
    const keyword = value.trim().toLowerCase();
    if (!keyword) return [];
    return index.filter((item) => item.label.toLowerCase().includes(keyword)).slice(0, 6);
  }, [index, value]);

  const goSearch = () => {
    const keyword = value.trim();
    if (!keyword) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    goSearch();
  };

  const isHero = variant === 'hero';

  return (
    <div ref={wrapRef} className="relative w-full">
      <form onSubmit={onSubmit} className="relative w-full">
        <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
          <i className="ri-search-line text-base"></i>
        </span>
        <input
          type="text"
          name="q"
          value={value}
          autoFocus={autoFocus}
          onChange={(event) => {
            setValue(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="搜索资讯、活动、角色或攻略…"
          className={`w-full rounded-full border text-sm outline-none transition-all placeholder:text-foreground-400 ${
            isHero
              ? 'border-background-50/30 bg-background-50/10 py-3 pl-11 pr-4 text-background-50 backdrop-blur-sm placeholder:text-background-50/70 focus:border-background-50/60'
              : 'border-background-300 bg-background-50 py-2 pl-10 pr-4 text-foreground-800 focus:border-primary-400 focus:ring-2 focus:ring-primary-100'
          }`}
        />
      </form>

      {open && suggestions.length > 0 ? (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1.5 shadow-lg">
          {suggestions.map((item) => (
            <button
              key={item.to}
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(item.to);
              }}
              className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-primary-50"
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-primary-500">
                <i className="ri-search-line text-sm"></i>
              </span>
              <span className="flex-1 truncate text-sm text-foreground-800">{item.label}</span>
              <span className="shrink-0 rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-label text-secondary-900">
                {item.type}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}